<script lang="ts" setup>
import { formatNumber, formatNumberWithDecimal } from 'cesg-shared';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StateInput from '../../inputs/state-input/state-input.vue';
import {
  useLiftPlanUELogic,
  type UseLiftPlanUEEmit,
  type UseLiftPlanUELogicProps,
} from './logic/useLiftPlanUELogic';
import { LiftTable } from './sub-components/lift-table';
import { SectionCard } from './sub-components/section-card';

export type LiftPlanUEProps = UseLiftPlanUELogicProps;
export type LiftPlanUEEmit = UseLiftPlanUEEmit;

const props = defineProps<LiftPlanUEProps>();
const emit = defineEmits<LiftPlanUEEmit>();
const { state, action } = useLiftPlanUELogic(props, emit);
const { t } = useI18n();

const sectionCardProps = computed(() => ({
  name: state.value.liftPlan.name,
  isEditing: state.value.isEditing,
  isSaving: state.value.isSaving,
  componentState: state.value.componentState.sectionCard,
}));

const liftTableProps = computed(() => ({
  columns: state.value.columns,
  subtotalColumns: state.value.subtotalColumns,
  ataColumns: state.value.ataColumns,
  componentState: state.value.componentState.liftTable,
  entries: state.value.liftPlan.entries,
  subtotalRows: state.value.liftPlan.subtotalRows,
  ataRows: state.value.liftPlan.ataRows,
  totalAmount: state.value.liftPlan.totalAmount,
  totalRetention: state.value.liftPlan.totalRetention,
  totalAmountDue: state.value.liftPlan.totalAmountDue,
}));
</script>

<template>
  <SectionCard
    v-bind="sectionCardProps"
    @toggle-edit="action.toggleEdit()"
    @cancel="action.cancel()"
    @delete="action.delete()"
    @update-name="action.updateName({ value: $event })"
  >
    <!-- Summary bar -->
    <div class="summary-bar">
      <div class="eq-table">
        <div class="eq-row">
          <span class="eq-label">{{
            t('projects.liftPlanUE.summaryBar.contractSum')
          }}</span>
          <span class="eq-value">
            <StateInput
              :mode="state.componentState.contractSum"
              class="inline-input summary-amount-input"
              readonly-class="summary-amount-input"
              inputmode="numeric"
              :model-value="formatNumber(state.liftPlan.contractSum)"
              @update:model-value="
                action.updatePlan({ field: 'contractSum', value: $event })
              "
            />
            kr
          </span>
        </div>
        <div
          class="eq-row"
          v-if="state.componentState.entreprenadsumma !== 'hidden'"
        >
          <span class="eq-label">{{
            t('projects.liftPlanUE.summaryBar.entreprenadsumma')
          }}</span>
          <span class="eq-value">
            <StateInput
              :mode="state.componentState.entreprenadsumma"
              class="inline-input summary-amount-input"
              readonly-class="summary-amount-input"
              inputmode="numeric"
              :model-value="formatNumber(state.liftPlan.entreprenadsumma)"
            />
            kr
          </span>
        </div>
        <div class="eq-row eq-op-row">
          <span class="eq-label">{{
            t('projects.liftPlanUE.summaryBar.retention')
          }}</span>
          <span class="eq-value">
            <StateInput
              :mode="state.componentState.retentionPercentage"
              class="inline-input pct-input"
              readonly-class="pct-input"
              inputmode="decimal"
              :model-value="
                formatNumberWithDecimal(state.liftPlan.retentionPercentage, 1)
              "
              @update:model-value="
                action.updatePlan({
                  field: 'retentionPercentage',
                  value: $event,
                })
              "
            />
            %
          </span>
        </div>
        <div class="eq-divider"></div>
        <div class="eq-row eq-result-row">
          <span class="eq-label">{{
            t('projects.liftPlanUE.summaryBar.retentionTotal')
          }}</span>
          <span class="eq-value eq-result">
            <StateInput
              :mode="state.componentState.retentionAmount"
              class="inline-input summary-amount-input"
              readonly-class="summary-amount-input"
              inputmode="numeric"
              :model-value="
                formatNumber(
                  Math.round(
                    state.liftPlan.contractSum *
                      (state.liftPlan.retentionPercentage / 100),
                  ),
                )
              "
            />
            kr
          </span>
        </div>
      </div>
    </div>

    <LiftTable
      v-bind="liftTableProps"
      @add-entry="action.addEntry()"
      @delete-entry="(id) => action.deleteEntry({ id })"
      @update-entry="(id, field, value) => action.updateEntry(id, field, value)"
      @add-subtotal="action.addSubtotal()"
      @delete-subtotal="(id) => action.deleteSubtotal({ id })"
      @update-subtotal="
        (id, field, value) => action.updateSubtotal(id, field, value)
      "
      @add-ata="action.addAta()"
      @delete-ata="(id) => action.deleteAta({ id })"
      @update-ata="(id, field, value) => action.updateAta(id, field, value)"
    />
  </SectionCard>
</template>

<style scoped>
.inline-input {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  transition: box-shadow 0.15s;
}

.inline-input:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.inline-input:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.pct-input {
  display: inline-block;
  width: 4rem;
  text-align: right;
}

.summary-amount-input {
  display: inline-block;
  width: 8rem;
  text-align: right;
}

:deep(.section-card-content) {
  background-color: #fafafc;
}

.summary-bar {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafc;
}

.eq-table {
  display: inline-grid;
  grid-template-columns: auto auto;
  gap: 0;
}

.eq-row {
  display: contents;
}

.eq-row > .eq-label,
.eq-row > .eq-value {
  padding: 0.35rem 0.75rem 0.35rem 0;
  font-size: 0.9rem;
  color: #444;
  white-space: nowrap;
}

.eq-row > .eq-value {
  text-align: right;
  font-weight: 600;
  color: #222;
  padding-right: 0;
  padding-left: 2.5rem;
}

.eq-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #555;
}

.eq-divider {
  grid-column: 1 / -1;
  border-top: 1px solid #d0d0d6;
  margin: 0.3rem 0;
}

.eq-result {
  color: var(--primary-color) !important;
  font-weight: 700;
  font-size: 1rem;
}

.eq-result-row > .eq-label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

:deep(.section-card-content) .table {
  background-color: #fff;
}
</style>
